import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { colorTextMuted } from "src/design/tokens";
import { useGrantSubscription } from "src/data/admin/queries/subscription";
import { GrantSubscriptionRequest } from "src/data/admin/types/subscription";
import { formatRestaurantLabel } from "src/business/restaurant/restaurantLabel";

export type GrantRestaurantOption = {
  id: string;
  name: string;
  city: string;
};

type ExpirationMode = "duration" | "date";

interface GrantSubscriptionDialogProps {
  open: boolean;
  /** Restaurants that can receive a subscription, for the picker. */
  restaurantOptions: GrantRestaurantOption[];
  /** Pre-selects a restaurant and hides the picker. */
  presetRestaurant?: GrantRestaurantOption | null;
  /** Tier names from the public catalog. */
  tiers: string[];
  onClose: () => void;
  onGranted: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Attaches a subscription to a restaurant (`POST`). On a restaurant that already
 * has one this overwrites tier, expiration and auto-renewal.
 */
export const GrantSubscriptionDialog = ({
  open,
  restaurantOptions,
  presetRestaurant = null,
  tiers,
  onClose,
  onGranted,
  onError,
}: GrantSubscriptionDialogProps) => {
  const [restaurant, setRestaurant] = useState<GrantRestaurantOption | null>(
    presetRestaurant,
  );
  const [tier, setTier] = useState("");
  const [expirationMode, setExpirationMode] =
    useState<ExpirationMode>("duration");
  const [durationMonths, setDurationMonths] = useState("1");
  const [expiresOn, setExpiresOn] = useState<Dayjs | null>(
    dayjs().add(1, "month"),
  );
  const [autoRenewal, setAutoRenewal] = useState(true);

  // Reset to a clean form every time the dialog is opened.
  useEffect(() => {
    if (!open) return;
    setRestaurant(presetRestaurant);
    setTier("");
    setExpirationMode("duration");
    setDurationMonths("1");
    setExpiresOn(dayjs().add(1, "month"));
    setAutoRenewal(true);
  }, [open, presetRestaurant]);

  // Preselect the first tier, whenever the catalog lands — before or after the
  // dialog is opened.
  useEffect(() => {
    setTier((current) => current || (tiers[0] ?? ""));
  }, [open, tiers]);

  const grantMutation = useGrantSubscription({
    onSuccess: (subscription) => {
      onGranted(
        `${subscription.restaurantName} is now on the "${subscription.tier}" tier.`,
      );
      onClose();
    },
  });

  const months = Number(durationMonths);
  const isDurationValid =
    expirationMode !== "duration" || (Number.isInteger(months) && months >= 1);
  const isDateValid =
    expirationMode !== "date" || (expiresOn !== null && expiresOn.isValid());
  const canSubmit = !!restaurant && !!tier && isDurationValid && isDateValid;

  const handleSubmit = () => {
    if (!canSubmit || !restaurant) return;

    const payload: GrantSubscriptionRequest =
      expirationMode === "date" && expiresOn
        ? {
            tier,
            nextBillingCycle: expiresOn.format("YYYY-MM-DD"),
            autoRenewal,
          }
        : { tier, durationMonths: months, autoRenewal };

    grantMutation.mutate(
      { restaurantId: restaurant.id, payload },
      {
        onError: (error) =>
          onError(
            error instanceof Error
              ? error.message
              : "Failed to grant the subscription",
          ),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => !grantMutation.isPending && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {presetRestaurant
          ? `Grant a subscription to ${formatRestaurantLabel(
              presetRestaurant.name,
              presetRestaurant.city,
            )}`
          : "Grant a subscription"}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {!presetRestaurant && (
              <Grid item xs={12}>
                <Autocomplete
                  options={restaurantOptions}
                  value={restaurant}
                  onChange={(_event, value) => setRestaurant(value)}
                  getOptionLabel={(option) =>
                    formatRestaurantLabel(option.name, option.city)
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Restaurant" size="small" />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tier</InputLabel>
                <Select
                  label="Tier"
                  value={tier}
                  onChange={(event) => setTier(event.target.value)}
                >
                  {tiers.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRenewal}
                    onChange={(event) => setAutoRenewal(event.target.checked)}
                  />
                }
                label="Auto-renewal"
              />
            </Grid>

            <Grid item xs={12}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={expirationMode}
                onChange={(_event, value: ExpirationMode | null) =>
                  value && setExpirationMode(value)
                }
              >
                <ToggleButton value="duration">For a duration</ToggleButton>
                <ToggleButton value="date">Until a date</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} md={6}>
              {expirationMode === "duration" ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Duration (months)"
                  value={durationMonths}
                  onChange={(event) => setDurationMonths(event.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  error={!isDurationValid}
                  helperText={
                    isDurationValid
                      ? "Counted from today"
                      : "Must be a whole number of at least 1"
                  }
                />
              ) : (
                <DatePicker
                  label="Expires on"
                  value={expiresOn}
                  onChange={(value) => setExpiresOn(value)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: colorTextMuted }}>
                  Access is cut 3 days after the expiration date — the grace
                  period. Granting on a restaurant that already has a
                  subscription overwrites its tier, expiration and auto-renewal.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={grantMutation.isPending}
        >
          Cancel
        </CustomButton>
        <CustomLoadingButton
          loading={grantMutation.isPending}
          disabled={!canSubmit}
          onSubmit={handleSubmit}
          label="Grant"
          color="success"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
